import { HTMLAttributes } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Container, StyledFormLabel, StyledFormHelperText } from "../CommonStyles";
import { AutoComplete, Checkbox, Typography } from "@kaltura/ds-react-components";
import { LabelledValue } from "@kaltura/mediaspace-shared-types";
import { StyledChip, StyledLi } from "./styled-components";
import { AutocompleteRenderGetTagProps } from "@mui/material";
import { Close16Icon } from "@kaltura/ds-react-icons";
import { FormFieldError } from "./../../form";

/**
 * Base props common to both single and multiple selection variants
 */
interface BaseAutocompleteProps {
    /**
     * The field ID and name in the form
     */
    id: string;

    /**
     * Label text for the autocomplete field
     */
    label: string;

    /**
     * Array of options to display in the dropdown
     */
    options: LabelledValue[];

    /**
     * Whether the field is required
     */
    required?: boolean;

    /**
     * Whether the field is disabled
     */
    disabled?: boolean;

    /**
     * Optional description text displayed below the label
     */
    description?: string;

    /**
     * Optional className for styling
     */
    className?: string;

    /**
     * Whether to display description as tooltip (default) or as text
     */
    descriptionTooltip?: boolean;

    /**
     * Optional onChange callback
     */
    onChange?: (value: any) => void;

    fontWeightBold?: boolean;
    inline?: boolean;

    /**
     * ID of the element that describes this section for accessibility
     */
    "aria-describedby"?: string;
}

/**
 * Props for single selection autocomplete
 */
interface SingleAutocompleteProps extends BaseAutocompleteProps {
    /**
     * Whether to allow multiple selections
     */
    multiple?: false;

    /**
     * Default value for single selection (string value)
     */
    defaultValue?: string;
}

/**
 * Props for multiple selection autocomplete
 */
interface MultipleAutocompleteProps extends BaseAutocompleteProps {
    /**
     * Whether to allow multiple selections
     */
    multiple: true;

    /**
     * Default value for multiple selections (array of string values)
     */
    defaultValue?: string[];
}

/**
 * Union type for Autocomplete props - discriminated by the 'multiple' field
 */
export type AutocompleteFieldProps = SingleAutocompleteProps | MultipleAutocompleteProps;

/**
 * custom options renderer
 * @param props
 * @param option
 * @param selected
 */
const renderOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: LabelledValue,
    { selected }: { selected: boolean }
) => {
    return (
        <StyledLi {...props} key={option.value}>
            <Checkbox checked={selected} />
            <Typography variant={"body2Highlight"}>{option.label}</Typography>
        </StyledLi>
    );
};

/**
 * custom selected options renderer
 * @param tagValue
 * @param getTagProps
 */
const renderTags = (tagValue: LabelledValue[], getTagProps: AutocompleteRenderGetTagProps) => {
    return tagValue.map((option: LabelledValue, index: number) => {
        const { key, ...tagProps } = getTagProps({ index });

        return <StyledChip key={key} deleteIcon={<Close16Icon />} label={option.label} {...tagProps} />;
    });
};

/**
 * Autocomplete field for forms that integrates with react-hook-form.
 * Displays a searchable dropdown with options from LabelledValue array.
 * Stores only the value (not the full LabelledValue) in the form.
 * Supports both single and multiple selections.
 */
export const AutocompleteField = ({
    className,
    id,
    label,
    description,
    options,
    required = false,
    disabled = false,
    descriptionTooltip = true,
    onChange,
    multiple = false,
    defaultValue,
    fontWeightBold = false,
    inline = false,
    "aria-describedby": ariaDescribedBy,
}: AutocompleteFieldProps) => {
    const { control } = useFormContext();

    // Custom function to get the label from LabelledValue
    const getOptionLabel = (option: LabelledValue | string): string => {
        if (typeof option === "string") {
            // Fallback for string values
            return option;
        }
        return option.label || "";
    };

    // Custom function to determine if option equals value
    const isOptionEqualToValue = (option: LabelledValue, value: any): boolean => {
        // Both option and value are LabelledValue objects, compare their value fields
        if (!value) {
            return false;
        }
        return option.value === value.value;
    };

    // Find the full LabelledValue object(s) from the stored primitive value(s)
    const getOptionFromValue = (value: any): LabelledValue | LabelledValue[] | null => {
        if (multiple) {
            // For multiple selections, value should be an array
            if (!Array.isArray(value) || value.length === 0) {
                return [];
            }
            return value
                .map((val: any) => options.find((opt) => opt.value === val))
                .filter((opt): opt is LabelledValue => opt !== undefined);
        }
        else {
            // For single selection
            if (value === null || value === undefined || value === "") {
                return null;
            }
            return options.find((opt) => opt.value === value) || null;
        }
    };

    const handleChange = (value: any) => {
        onChange?.(value);
    };

    let describedBy = !descriptionTooltip && description ? `${id}-description ` : "";
    if (ariaDescribedBy) {
        describedBy += ariaDescribedBy;
    }

    return (
        <Container className={className} inline={inline}>
            <StyledFormLabel
                htmlFor={id}
                text={label}
                description={descriptionTooltip ? description : undefined}
                required={required}
                id={`${id}-label`}
                fontWeightBold={fontWeightBold}
            />
            {!descriptionTooltip && description && (
                <StyledFormHelperText id={`${id}-description`}>{description}</StyledFormHelperText>
            )}
            <Controller
                control={control}
                name={id as "dummy"}
                defaultValue={defaultValue ?? (multiple ? [] : "")}
                rules={{ required: required }}
                render={({ field: { onChange: onFieldChange, onBlur, value, ref }, fieldState: { error } }) => (
                    <>
                        <AutoComplete
                            inputRef={ref}
                            inputLabel=""
                            options={options}
                            value={getOptionFromValue(value)}
                            onChange={(_event, newValue) => {
                                // Store only the value(s) in the form, not the full LabelledValue object(s)
                                let primitiveValue: any;
                                if (multiple) {
                                    // For multiple selections, store an array of primitive values
                                    primitiveValue = Array.isArray(newValue) ? newValue.map((item) => item.value) : [];
                                }
                                else {
                                    // For single selection, store the primitive value
                                    primitiveValue = newValue ? newValue.value : "";
                                }
                                onFieldChange(primitiveValue);
                                handleChange(primitiveValue);
                            }}
                            onBlur={onBlur}
                            getOptionLabel={getOptionLabel}
                            isOptionEqualToValue={isOptionEqualToValue}
                            disabled={disabled}
                            multiple={multiple}
                            fullWidth={true}
                            placeholder={label}
                            aria-describedby={describedBy}
                            aria-labelledby={`${id}-label`}
                            renderOption={multiple ? renderOption : undefined}
                            renderTags={renderTags}
                        />
                        {error?.message && <FormFieldError>{error.message}</FormFieldError>}
                    </>
                )}
            />
        </Container>
    );
};
